import AddIcon from '@mui/icons-material/Add';
import { Box, Divider, Typography } from '@mui/material';

import { DataGrid, ProgressModal } from '../../../components';
import HorizontalBox from '../../../components/Boxes/HorizontalBox/HorizontalBox';
import ButtonWithOptions from '../../../components/ButtonWithOptions/ButtonWithOptions';
import { BankConnectionPanel } from './bankConnectionPanel';
import {
  BankFeedView,
  dataListSim,
  NewBankFeedOptions,
} from './bankFeedsList.defaults';
import { useBankFeedsList } from './bankFeedsList.hooks';
import {
  BankFeedWrapperBox,
  ButtonBox,
  HeaderRow,
  IconBox,
  SkeletonBox,
  SkeletonCircular,
  SkeletonRectangle,
  SkeletonText,
  ViewOptionChip,
  ViewOptionsBox,
} from './bankFeedsList.styles';

const tempSkeletonItem = (num: number) => {
  const skeletons = [];

  while (num > 0) {
    skeletons.push(
      <SkeletonBox key={num}>
        <SkeletonText variant="text" />
        <SkeletonCircular variant="circular" />
        <SkeletonRectangle variant="rectangular" />
        <SkeletonRectangle variant="rectangular" />
      </SkeletonBox>
    );
    num--;
  }
  return skeletons;
};

export const BankFeedsList: React.FC = () => {
  const {
    viewState,
    setViewState,
    handleNewButtonAction,
    showBankConnectionPanel,
    onBankConnectionClose,
    isLoading,
    headerList,
    activeHeaderFields,
    handleUpdateHeader,
    onColumnOrderChange,
    bankFeedsSelectionModel,
    setBankFeedsSelectionModel,
    handleFilter,
    handleOnView,
    onDetailsClose,
  } = useBankFeedsList();

  return (
    <BankFeedWrapperBox id="bank_feed_list_main" role="main">
      <ProgressModal
        id="loading_modal_bank_feeds_list"
        showProgress={isLoading}
      />
      <HeaderRow>
        <IconBox>
          <Typography variant="pageTitle">Bank Feeds</Typography>
          <ViewOptionsBox>
            <ViewOptionChip
              size="small"
              label={BankFeedView.DASHBOARD}
              color={
                viewState === BankFeedView.DASHBOARD ? 'primary' : 'secondary'
              }
              clickable
              onClick={() => setViewState(BankFeedView.DASHBOARD)}
            />
            <Divider orientation="vertical" variant="middle" flexItem />
            <ViewOptionChip
              size="small"
              label={BankFeedView.HYBRID}
              color={
                viewState === BankFeedView.HYBRID ? 'primary' : 'secondary'
              }
              clickable
              onClick={() => setViewState(BankFeedView.HYBRID)}
            />
            <Divider orientation="vertical" variant="middle" flexItem />
            <ViewOptionChip
              size="small"
              label={BankFeedView.DATA_VIEW}
              color={
                viewState === BankFeedView.DATA_VIEW ? 'primary' : 'secondary'
              }
              clickable
              onClick={() => setViewState(BankFeedView.DATA_VIEW)}
            />
          </ViewOptionsBox>
        </IconBox>
        <ButtonBox>
          <ButtonWithOptions
            buttonID="add_new_bank_feeds_button"
            popoverID="add_new_bank_popover"
            onClick={handleNewButtonAction}
            buttonLabel="Add New"
            buttonIcon={<AddIcon />}
            options={NewBankFeedOptions}
          />
        </ButtonBox>
      </HeaderRow>
      {viewState !== BankFeedView.DATA_VIEW ? (
        <HorizontalBox>{tempSkeletonItem(3)}</HorizontalBox>
      ) : (
        <div></div>
      )}
      {viewState === BankFeedView.DASHBOARD ? (
        <Box>
          <HorizontalBox>
            <SkeletonBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
            </SkeletonBox>
            <SkeletonBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
            </SkeletonBox>
            <SkeletonBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
              <HorizontalBox>{tempSkeletonItem(2)}</HorizontalBox>
            </SkeletonBox>
          </HorizontalBox>
        </Box>
      ) : (
        <DataGrid
          id="bank_feeds_data_grid"
          dataList={dataListSim || []}
          minHeight="1vh"
          autoHeight={false}
          headerList={headerList}
          handleOnView={handleOnView}
          selectionModel={bankFeedsSelectionModel}
          setSelectionModel={setBankFeedsSelectionModel}
          activeHeaderFields={activeHeaderFields}
          handleUpdateHeader={handleUpdateHeader}
          handleFilter={handleFilter}
          onColumnOrderChange={onColumnOrderChange}
        />
      )}

      {showBankConnectionPanel && (
        <BankConnectionPanel
          showBankConnectionPanel={showBankConnectionPanel}
          onBankConnectionClose={onBankConnectionClose}
        />
      )}
    </BankFeedWrapperBox>
  );
};
